import { useEffect, useState, useRef } from "@wordpress/element";
import "./style.css";
import NineDots from "./icons/NineDotsIcon";
import WarningIcon from "./icons/WarningIcon";
import Expand from "./icons/ExpandIcon";
import CloseSmall from "./icons/CloseSmallIcon";
import SendIcon from "./icons/SendIcon";
import PhotoUploadIcon from "./icons/PhotoUploadIcon";
import DialogBubble from "./icons/DialogBubble";

import { useSpring, animated } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import EmojiFreeTextarea from "./components/EmojiFreeTextarea";

import DOMPurify from "dompurify";

import { useGlobalStore } from "./index";

import logo from "./assets/logo.png";

const uploadFile = async (file, accessToken, claimId = "defaultClaimId") => {
  try {
    const bucketName = "dfcx-claims-images";
    const objectName = `${claimId}/${encodeURIComponent(file.name)}`;
    const uploadUrl = `https://storage.googleapis.com/upload/storage/v1/b/${bucketName}/o?uploadType=media&name=${objectName}`;

    const response = await fetch(uploadUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/octet-stream",
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    const data = await response.json();
    console.log("Upload successful:", data);
  } catch (error) {
    console.error("Error uploading file:", error);
  }
};

function linkify(text) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(
    urlRegex,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );
}

function createMessage(incMessage, speaker, isImage = false) {
  const message = {};

  if (typeof incMessage === "string") {
    message.text = incMessage;
  } else {
    const messageText = incMessage?.queryResult?.responseMessages?.map(
      (x) => x?.text?.text[0]
    );
    message.text = Array.isArray(messageText)
      ? DOMPurify.sanitize(linkify(messageText.join("<br />"))).replace(
          /\n/g,
          "<br />"
        )
      : "Something has gone wrong, please try again.";
  }
  message.timestamp = getFormattedTime();
  message.speaker = speaker;
  message.isImage = isImage;
  return message;
}

async function sendMessageToLanguageDetection(token, text = "") {
  const url =
    "https://translate.googleapis.com/v3/projects/fcp-call-center:detectLanguage";
  const requestBody = {
    content: text,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });
    const data = await response.json();
    console.log("Language detection response:", data);

    return data;
  } catch (error) {
    console.error("Error detecting language:", error);
  }
}

function useViewportSize() {
  const [viewportSize, setViewportSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return viewportSize;
}

function scrollTop() {
  document.body.scrollTop = document.documentElement.scrollTop = 0;
}

function getFormattedTime() {
  return new Date().toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function useDebounce(callback, delay) {
  const timeoutRef = useRef();

  return React.useCallback(
    (...args) => {
      clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );
}

function DraggableSticky({ children, isOpen }) {
  const { width } = useViewportSize();
  const [position, setPosition] = useState(window.innerWidth - 500); // Start in the center
  const containerRef = useRef(null);

  const [{ x }, api] = useSpring(() => ({
    x: position,
    config: { tension: 200, friction: 20 },
  }));

  // Smooth transition when resizing the viewport
  const updatePositionOnResize = () => {
    const newPosition = window.innerWidth - 500; // Adjust as needed
    setPosition(newPosition);
    api.start({ x: newPosition, config: { tension: 250, friction: 30 } }); // Smooth transition
  };

  useEffect(() => {
    window.addEventListener("resize", updatePositionOnResize);
    return () => window.removeEventListener("resize", updatePositionOnResize);
  }, []);

  const bind = useDrag(({ movement: [mx], down, event }) => {
    // Allow dragging only if event started from .drag-handle
    const target = event.target;
    const handle = containerRef.current?.querySelector(".drag-handle");

    if (handle && handle.contains(target)) {
      if (down) {
        api.start({ x: mx + position });
      } else {
        // Snap to closest side
        const snapX =
          mx + position < window.innerWidth / 2
            ? 0
            : window.innerWidth -
              containerRef.current
                ?.querySelector(".chatbot-header")
                .getBoundingClientRect().width -
              50;
        setPosition(snapX);
        api.start({ x: snapX });
      }
    }
  });

  return (
    <>
      {width > 768 ? (
        <animated.div
          ref={containerRef}
          {...bind()}
          style={{
            position: "fixed",
            bottom: isOpen ? "20px" : "64px",
            borderRadius: "10px",
            x,
            marginLeft: "20px",
          }}
        >
          {children}
        </animated.div>
      ) : (
        <>{children}</>
      )}
    </>
  );
}

export default function App() {
  const {
    sessionId,
    messageHistory,
    setMessageHistory,
    getMessageHistory,
    clearMessageHistory,
    setLanguageCode,
    getLanguageCode,
    getSessionId,
  } = useGlobalStore();
  const { width } = useViewportSize();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isActiveSession, setIsActiveSession] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [inputValue, setInputValue] = useState("");
  const [initialTime, setInitialTime] = useState(null);
  const [token, setToken] = useState(null);
  const [showExitOptions, setShowExitOptions] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);

  const [timeoutTimestamp, setTimeoutTimestamp] = useState(null);

  const [isError, setIsError] = useState(null);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);

  const [promptClosed, setPromptClosed] = useState(false);
  const [promptOpacity, setPromptOpacity] = useState(0);

  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);

  const chatWindowRef = useRef(null);
  const fileInputRef = useRef(null);

  const closePrompt = () => setPromptClosed(true);

  const startNewSession = async () => {
    console.log("startNewSession has fired");
    if (!token) {
      console.log("there was no token, but I am retrieving one now");
      return await fetch(
        "https://us-central1-fcp-call-center.cloudfunctions.net/get_token",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            API_KEY: window.CHATBOT_ENV?.API_KEY,
          }),
        }
      )
        .then(async (response) => {
          const data = await response.json();

          console.log(data);
          return data;
        })
        .then((data) => {
          // console.log("Success:", data);
          setToken(data.token);
          return data;
        })
        .catch((error) => console.error("Error:", error))
        .finally(() => {
          console.log(
            "finally: this message should always appear when a fetch is made to get the token"
          );
        });
    }
  };

  // Scroll to bottom when messages update
  useEffect(() => {
    // NOTE: This doesn't seem to work - causes multiple scrolls
    // if (chatEndRef.current) {
    //   chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    // }
    if (chatWindowRef.current) {
      // Use requestAnimationFrame to ensure scroll after DOM paints
      requestAnimationFrame(() => {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
      });
    }
  }, [messageHistory]);

  useEffect(() => {
    if (isOpen && isActiveSession) {
      if (chatWindowRef.current) {
        window.setTimeout(() => {
          // Use requestAnimationFrame to ensure scroll after DOM paints
          requestAnimationFrame(() => {
            // chatWindowRef.current.scrollTop =
            //   chatWindowRef.current.scrollHeight;
            chatWindowRef.current.scrollTo({
              top: chatWindowRef.current.scrollHeight,
              behavior: "smooth",
            });
          });
        }, 100);
      }
    }
  }, [isOpen, isActiveSession]);

  useEffect(() => {
    if (showExitOptions) {
      if (chatWindowRef.current) {
        window.setTimeout(() => {
          // Use requestAnimationFrame to ensure scroll after DOM paints
          requestAnimationFrame(() => {
            // chatWindowRef.current.scrollTop =
            //   chatWindowRef.current.scrollHeight;
            chatWindowRef.current.scrollTo({
              top: chatWindowRef.current.scrollHeight,
              behavior: "smooth",
            });
          });
        }, 100);
      }
    }
  }, [showExitOptions]);

  useEffect(() => {
    if (width <= 768 && isOpen) {
      scrollTop();
      document.querySelector("body").style.height = "100%";
      document.querySelector("body").style.overflow = "hidden";
      document.querySelector("html").style.height = "100%";
    } else {
      document.querySelector("body").style.height = "";
      document.querySelector("body").style.overflow = "";
      document.querySelector("html").style.height = "";
    }
  }, [width, isOpen]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setIsExpanded(false);
    setIsActiveSession(true);
    setPromptClosed(true);

    if (isOpen) {
      document.getElementById("fl-to-top").style.display = "none";
    } else {
      document.getElementById("fl-to-top").style.display = "inline";
    }
  };

  const handleSend = async (incToken) => {
    if (getLanguageCode() === null) {
      let langCode;

      await sendMessageToLanguageDetection(token, inputValue).then((data) => {
        console.log(data);
        setLanguageCode(data.languages[0].languageCode);
        langCode = data.languages[0].languageCode;
      });
      if (inputValue.trim()) {
        setMessageHistory([
          ...getMessageHistory(),
          createMessage(inputValue, "user"),
          createMessage("...", "chatbot"),
        ]);
        await sendMessageToDialogflow(
          incToken,
          inputValue,
          undefined,
          sessionId,
          langCode
        )
          .then((data) => {
            setMessageHistory([
              ...getMessageHistory(),
              createMessage(inputValue, "user"),
              createMessage(data, "chatbot"),
            ]);
          })
          .catch((error) => {
            console.error("error time :(");
            console.log(error);
            setIsError(error);
          });
        setInputValue("");
      }
    } else {
      if (inputValue.trim()) {
        setMessageHistory([
          ...getMessageHistory(),
          createMessage(inputValue, "user"),
          createMessage("...", "chatbot"),
        ]);
        await sendMessageToDialogflow(
          incToken,
          inputValue,
          undefined,
          sessionId,
          getLanguageCode()
        )
          .then((data) => {
            setMessageHistory([
              ...getMessageHistory(),
              createMessage(inputValue, "user"),
              createMessage(data, "chatbot"),
            ]);
          })
          .catch((error) => {
            console.error("error time :(");
            console.log(error);
            setIsError(error);
          });
        setInputValue("");
      }
    }
  };

  const handleMouseOver = useDebounce(() => {
    setIsExpanded(true);
  }, 100);

  async function sendMessageToDialogflow(
    token,
    text = "",
    intent,
    sessionId,
    languageCode
  ) {
    const SESSION_ID = sessionId;
    const PROJECT_ID = "fcp-call-center";

    const LANGUAGE_CODE = "en";
    // const AGENT_ID = "77774c2c-39fd-4012-91ed-da10a4543cd2";
    const AGENT_ID = "5054598c-a7bd-4d82-8070-553c4d61a9ef";
    const url = `https://dialogflow.googleapis.com/v3beta1/projects/${PROJECT_ID}/locations/global/agents/${AGENT_ID}/sessions/${SESSION_ID}:detectIntent`;
    const requestBody = {
      queryInput: {
        languageCode: LANGUAGE_CODE,
      },
    };

    if (intent !== undefined) {
      requestBody.queryInput.intent = {
        intent: `projects/${PROJECT_ID}/locations/global/agents/${AGENT_ID}/intents/${intent}`,
      };
    }

    if (text) {
      requestBody.queryInput.text = {
        text: text,
      };
    }

    if (languageCode) {
      requestBody.queryInput.languageCode = languageCode;
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();

      if (response.status === 401) {
        if (
          data?.error?.details &&
          data.error.details[0]?.reason === "ACCESS_TOKEN_EXPIRED"
        ) {
          console.error(
            "Error: Access token has expired. Please refresh the token."
          );
          throw "Access token has expired.";
        } else {
          console.error("Error 401: Unauthorized access.", data);
        }
      }

      if (response.status === 400 || response.status === 500) {
        console.error(data.error.details[0]?.reason);
        throw String(data.error.details[0]?.reason);
      }

      console.log("Dialogflow Response:", data);
      return data;
    } catch (error) {
      console.error("Error sending message to Dialogflow:", error);
      throw error;
    }
  }

  // const handleFileChange = (event) => {
  //   const selectedFile = event.target.files[0];

  //   if (selectedFile) {
  //     setFile(selectedFile);
  //     setPreview(URL.createObjectURL(selectedFile)); // Generate preview URL
  //     return URL.createObjectURL(selectedFile);
  //   }
  // };

  // const uploadFile = async () => {
  //   await onChange();
  // };

  return (
    <>
      {!isOpen && !isActiveSession && (
        <>
          {!promptClosed && (
            <div
              className="Prompt"
              onMouseOver={() => setPromptOpacity(1)}
              onMouseOut={() => setPromptOpacity(0)}
            >
              <button
                style={{
                  opacity: promptOpacity,
                }}
                className="Prompt__CloseButton"
                onClick={() => closePrompt()}
              >
                Close
              </button>
              <div className="Prompt__MessageBubble">
                <div className="Prompt__PromptText">
                  <img src={logo} alt="Plugin Logo" />
                  <div>
                    <p style={{ margin: 0 }}>
                      Hello! 👋 How can I help you today?
                    </p>
                    <p style={{ margin: 0 }}>¡Hablamos español!</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div
            className={`chatbot-container ${isOpen ? "open" : ""}`}
            onMouseEnter={handleMouseOver}
            onMouseLeave={() => setIsExpanded(false)}
            style={{
              borderRadius: isOpen || isExpanded ? "8px" : "100%",
              transition: isOpen ? "none" : "border-radius 0.1s ease-in-out",
              position: "fixed",
              right: "20px",
              bottom: "64px",
            }}
          >
            <div
              className="chatbot-header"
              onClick={() => {
                setInitialTime(getFormattedTime());
                // startNewSession();
                setIsOpen(true);
                // document.getElementById("fl-to-top").style.display = "none";
                setIsExpanded(false);
                setIsActiveSession(true);
              }}
              style={{
                backgroundColor: isOpen ? "white" : "#b78242",
                color: isOpen ? "#004334" : "white",
              }}
            >
              <DialogBubble />
              <div
                class="container"
                style={{
                  marginLeft: isExpanded ? "25px" : "0",
                  display: "flex",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  width: isExpanded ? "140px" : "0px",
                  transition: "width 0.3s ease-in-out",
                }}
              >
                <span
                  class="extra"
                  style={{
                    fontSize: "16px",
                    opacity: isExpanded ? 1 : 0,
                    transform: isExpanded
                      ? "translateX(0)"
                      : "translateX(140px)",
                    transition:
                      "opacity 0.3s ease-in-out, transform 0.3s ease-in-out, margin-left 1s ease-in-out",
                  }}
                >
                  Chat with us
                </span>
              </div>
            </div>
          </div>
        </>
      )}
      <DraggableSticky isOpen={isOpen}>
        {!isOpen && isActiveSession && (
          <div
            className={"chatbot-container"}
            onMouseEnter={handleMouseOver}
            onMouseLeave={() => setIsExpanded(false)}
            style={{
              borderRadius: "8px",
            }}
          >
            <div
              className="chatbot-header"
              style={{
                backgroundColor: isOpen ? "white" : "#b78242",
                color: isOpen ? "#004334" : "white",
                display: "flex",
                alignItems: "center",
                width: "430px",
              }}
            >
              <div
                className="drag-handle"
                style={{ cursor: "grab", display: "flex" }}
              >
                <NineDots color={isOpen ? "#004334" : "white"} />
              </div>
              <span
                style={{
                  fontSize: "16px",
                }}
              >
                FCP Claims Assistant
              </span>
              <button style={{ display: "flex" }} onClick={toggleChat}>
                <Expand color={isOpen ? "#004334" : "white"} />
              </button>
            </div>
          </div>
        )}
        {isOpen && isActiveSession && (
          <>
            <div
              className={`chatbot-container ${isOpen ? "open" : ""}`}
              onMouseEnter={handleMouseOver}
              onMouseLeave={() => setIsExpanded(false)}
              style={{
                borderRadius: isOpen || isExpanded ? "8px" : "100%",
                transition: isOpen ? "none" : "border-radius 0.1s ease-in-out",
              }}
            >
              <div
                className="chatbot-header"
                style={{
                  backgroundColor: isOpen ? "white" : "#b78242",
                  color: isOpen ? "#004334" : "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "23px 20px",
                }}
              >
                <div
                  className="drag-handle"
                  style={{ cursor: "grab", display: "flex" }}
                >
                  <NineDots color={isOpen ? "#004334" : "white"} />

                  <span
                    style={{
                      marginLeft: "10px",
                      color: "var(--Green, #004334)",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: 700,
                      lineHeight: "24px",
                      letterSpacing: "0.16px",
                    }}
                  >
                    FCP Claims Assistant
                  </span>
                </div>
                <button style={{ display: "flex" }} onClick={toggleChat}>
                  <Expand color={isOpen ? "#004334" : "white"} />
                </button>
              </div>
              <>
                <div className="chatbot-body">
                  <div className="chatbot-messages" ref={chatWindowRef}>
                    <div className="message-container chatbot-message">
                      <div className="message">
                        Welcome to the FCP Claim Assistant! Here you can manage
                        all of your claims needs with FCP, including checking on
                        existing claims and their current status as well as
                        starting a new claim and inputing all the necessary
                        information to get your issues resolved. <br />
                        Para atención en español, escribe en español.
                      </div>
                      <div className="timestamp">Warren T • {initialTime}</div>
                    </div>
                    {getMessageHistory().length > 0 &&
                      getMessageHistory().map((msg, index) => (
                        <>
                          {msg.text !== "..." && !msg.isImage && (
                            <div
                              key={index}
                              className={`message-container ${
                                msg.speaker === "chatbot" ? "chatbot" : "user"
                              }-message`}
                            >
                              <div
                                className="message"
                                dangerouslySetInnerHTML={{
                                  __html: msg.text,
                                }}
                              />
                              <div className="timestamp">
                                {msg.speaker === "chatbot"
                                  ? "Warren T."
                                  : "You"}{" "}
                                • {msg.timestamp}
                              </div>
                            </div>
                          )}
                          <>
                            {msg.text !== "..." && msg.isImage && (
                              <div
                                key={index}
                                className={`message-container ${
                                  msg.speaker === "chatbot" ? "chatbot" : "user"
                                }-message`}
                              >
                                {/* <div className="message">{msg.text}</div> */}
                                <img
                                  src={msg.text}
                                  alt="Preview"
                                  style={{
                                    maxWidth: "300px",
                                    maxHeight: "300px",
                                    borderRadius: "8px",
                                  }}
                                />
                                <div className="timestamp">
                                  {msg.speaker === "chatbot"
                                    ? "Warren T."
                                    : "You"}{" "}
                                  • {msg.timestamp}
                                </div>
                              </div>
                            )}
                          </>
                        </>
                      ))}

                    {showExitOptions && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            gap: 10,
                          }}
                        >
                          <span
                            style={{
                              color: "var(--Gold, #B78242)",
                              textAlign: "center",
                              fontFamily: "Poppins",
                              fontSize: "12px",
                              fontStyle: "normal",
                              fontWeight: 700,
                              lineHeight: "20px",
                              letterSpacing: "0.12px",
                              alignSelf: "flex-end",
                            }}
                          >
                            Quick Responses
                          </span>
                          <button
                            className="cta-button"
                            style={{
                              borderRadius: "50px",
                              border: "1px solid var(--Gold, #B78242)",
                              textAlign: "center",
                              fontFamily: "Poppins",
                              fontSize: "16px",
                              fontStyle: "normal",
                              fontWeight: 400,
                              lineHeight: "24px" /* 150% */,
                              letterSpacing: "0.16px",
                              padding: "12px 20px",
                              alignSelf: "flex-end",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setIsActiveSession(false);
                              setIsOpen(false);
                              document.getElementById(
                                "fl-to-top"
                              ).style.display = "inline";
                              setShowExitOptions(false);
                              clearMessageHistory();
                            }}
                          >
                            Yes, leave this chat
                          </button>
                          <button
                            className="cta-button"
                            style={{
                              borderRadius: "50px",
                              border: "1px solid var(--Gold, #B78242)",
                              textAlign: "center",
                              fontFamily: "Poppins",
                              fontSize: "16px",
                              fontStyle: "normal",
                              fontWeight: 400,
                              lineHeight: "24px" /* 150% */,
                              letterSpacing: "0.16px",
                              padding: "12px 20px",
                              alignSelf: "flex-end",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setShowExitOptions(false);
                            }}
                          >
                            Nevermind
                          </button>
                        </div>
                      </>
                    )}

                    {/* TODO: need to check for type of error, maybe toggle for timeout specifically :think:  */}

                    {isError && (
                      <>
                        <div className="message-container chatbot-message">
                          {width > 768 && (
                            <div className="message">
                              {isError === "Access token has expired." ? (
                                <>
                                  This session has ended due to inactivity. You
                                  can either start a new session of close the
                                  chatbot window.
                                </>
                              ) : (
                                <>
                                  {isError}
                                  <br /> An error has occurred. Please try
                                  again.
                                </>
                              )}
                              <div
                                style={{
                                  position: "relative",
                                  transform: "translate(45px, 66%)",
                                }}
                              >
                                <WarningIcon />
                              </div>
                            </div>
                          )}
                          {width < 768 && (
                            <div
                              style={{
                                maxWidth: "100% !important",
                                justifyContent: "space-between",
                                display: "flex",
                                width: "100%",

                                alignItems: "flex-start",
                                gap: "10px",
                              }}
                            >
                              <div
                                style={{
                                  padding: "15px",
                                  borderRadius: "7px",
                                  background: "#fff",
                                  boxShadow:
                                    "0px 0px 4px 0px rgba(0, 0, 0, 0.25)",
                                  color: "#666",
                                  fontFamily: "Poppins",
                                  fontSize: "16px",
                                  fontStyle: "normal",
                                  fontWeight: 400,
                                  lineHeight: "24px" /* 150% */,
                                  letterSpacing: "0.16px",
                                }}
                              >
                                {isError === "Access token has expired." ? (
                                  <>
                                    This session has ended due to inactivity.
                                    You can either start a new session of close
                                    the chatbot window.
                                  </>
                                ) : (
                                  <>
                                    {isError}
                                    <br /> An error has occurred. Please try
                                    again.
                                  </>
                                )}
                              </div>
                              <div style={{ alignSelf: "anchor-center" }}>
                                <WarningIcon />
                              </div>
                            </div>
                          )}

                          <div className="timestamp">
                            Warren T • {timeoutTimestamp}
                          </div>
                        </div>
                      </>
                    )}

                    {isWaiting && (
                      <div
                        className="message-container chatbot-message"
                        style={{ width: "350px" }}
                      >
                        <span className="loading-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="chatbot-input">
                    <div
                      style={{
                        background: "#FFF",
                        width: "100%",
                        height: "100%",
                        border: "1px solid var(--Light-Grey, #E0E0E0)",
                        borderRadius: "20px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          flexDirection: "row",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <EmojiFreeTextarea
                          inputValue={inputValue}
                          setInputValue={setInputValue}
                          handleSend={async () => {
                            setInputValue("");
                            setIsWaiting(true);
                            console.log("the button was clicked");
                            if (!token) {
                              console.log(
                                "no token found, starting new session"
                              );
                              const newToken = await startNewSession();
                              await handleSend(newToken.token);
                              setToken(newToken.token);
                            } else {
                              console.log("token found");
                              await handleSend(token);
                            }
                            setIsWaiting(false);
                          }}
                        />

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginRight: "16px",
                            paddingTop: "16px",
                          }}
                        >
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              // const selectedFile = handleFileChange(e);
                              const selectedFile = e.target.files[0];

                              if (selectedFile) {
                                setFile(selectedFile);
                                setPreview(URL.createObjectURL(selectedFile)); // Generate preview URL
                                // return URL.createObjectURL(selectedFile);
                              }
                              console.log(selectedFile);
                              console.log(
                                "TODO: pass this stupid thing along so the name isn't undefined"
                              );

                              console.log(e.target.files[0].name);
                              setMessageHistory([
                                ...getMessageHistory(),
                                // createMessage(selectedFile, "user", true),
                                createMessage(
                                  URL.createObjectURL(selectedFile),
                                  "user",
                                  true
                                ),
                              ]);
                              await uploadFile(
                                selectedFile,
                                token,
                                getSessionId()
                              );
                            }}
                            ref={fileInputRef}
                            style={{ display: "none" }}
                          />
                          <button
                            onClick={async () => {
                              fileInputRef.current.click();
                            }}
                            style={{
                              padding: "10px 10px",
                              fontSize: "16px",
                              backgroundColor: "#b78242",
                              color: "white",
                              border: "none",
                              borderRadius: "5px",
                              cursor: "pointer",
                            }}
                          >
                            <PhotoUploadIcon />
                          </button>
                          <button
                            style={{
                              background: "none",
                              color: "inherit",
                              border: "none",

                              font: "inherit",
                              cursor: "pointer",
                              outline: "inherit",
                              alignSelf: "flex-end",
                              padding: "0 16px 6px 16px",
                            }}
                            onClick={async () => {
                              setInputValue("");
                              setIsWaiting(true);
                              console.log("the button was clicked");
                              if (!token) {
                                console.log(
                                  "no token found, starting new session"
                                );
                                const newToken = await startNewSession();
                                await handleSend(newToken.token);
                                setToken(newToken.token);
                              } else {
                                console.log("token found");
                                await handleSend(token);
                              }
                              setIsWaiting(false);
                            }}
                          >
                            <SendIcon />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="exit-cta">
                  <button
                    onClick={() => {
                      setShowExitOptions(true);
                    }}
                  >
                    <CloseSmall />
                    <div className="exit-message">Leave This Chat</div>
                  </button>
                </div>
              </>
            </div>
          </>
        )}
      </DraggableSticky>
    </>
  );
}
